<template>
    <Toast />
    <Dialog
        :visible="visible"
        @update:visible="$emit('update:visible', $event)"
        modal
        :header="formulario.id ? 'EDITAR RECETA' : 'NUEVA RECETA'"
        :style="{ width: '900px' }"
        class="p-fluid"
    >
        <div class="col-12">
            <div class="p-fluid formgrid grid">
                
                <!-- SECCIÓN PRODUCTO -->
                <div class="field col-12 md:col-6">
                    <span><strong>PRODUCTO: </strong> <span class="text-red-500 font-bold">*</span></span>
                    <DropdownProducto
                        id="producto_id"
                        v-model="formulario.producto_id"
                        tipoCatalogo="1"
                    />
                    <small class="p-invalid text-red-500" v-if="errors.producto_id">{{ errors.producto_id[0] }}</small>
                </div>

                <div class="field col-12 md:col-4">
                    <span><strong>DESCRIPCIÓN / NOTAS: </strong></span>
                    <InputText
                        id="descripcion"
                        v-model="formulario.descripcion"
                        placeholder="Ej. Receta estándar de verano"
                    />
                    <small class="p-invalid text-red-500" v-if="errors.descripcion">{{ errors.descripcion[0] }}</small>
                </div>

                <div class="field col-12 md:col-2">
                    <span><strong>ESTADO: </strong></span>
                    <div class="flex align-items-center mt-2">
                        <InputSwitch v-model="formulario.estado" inputId="estado" />
                        <label for="estado" class="ml-2 mb-0 font-bold text-600">{{ formulario.estado ? 'Activa' : 'Inactiva' }}</label>
                    </div>
                </div>

                <!-- SECCIÓN INSUMOS -->
                <div class="field col-12 mt-3">
                    <h5><strong>INSUMOS NECESARIOS ({{ formulario.detalles.length }})</strong></h5>
                    <hr>
                </div>

                <div class="col-12">
                     <div v-for="(detalle, index) in formulario.detalles" :key="index" class="p-fluid formgrid grid align-items-end border-bottom-1 surface-border">
                        
                        <div class="field col-12 md:col-4">
                            <span><strong>INSUMO: </strong> <span class="text-red-500 font-bold">*</span></span>
                            <DropdownProducto
                                v-model="detalle.producto_id"
                                tipoCatalogo="3"
                            />
                        </div>

                        <div class="field col-12 md:col-2">
                            <span><strong>GRAMOS: </strong></span>
                            <InputNumber
                                v-model="detalle.cantidad_gramo"
                                :minFractionDigits="0"
                                :maxFractionDigits="2"
                                placeholder="0"
                                suffix=" g"
                                :disabled="detalle.cantidad_unidad > 0"
                                :inputClass="'text-right'"
                            />
                        </div>

                        <div class="field col-12 md:col-2">
                            <span><strong>UNIDADES: </strong></span>
                            <InputNumber
                                v-model="detalle.cantidad_unidad"
                                :minFractionDigits="0"
                                :maxFractionDigits="2"
                                placeholder="0"
                                suffix=" un"
                                :disabled="detalle.cantidad_gramo > 0"
                                :inputClass="'text-right'"
                            />
                        </div>

                        <div class="field col-12 md:col-2" v-if="false">
                            <span><strong>APLICA MERMA: </strong></span>
                            <div class="flex align-items-center mt-2">
                                <Checkbox v-model="detalle.aplica_merma" :binary="true" :inputId="'merma' + index" />
                                <label :for="'merma' + index" class="ml-2 mb-0 font-bold text-600">Sí</label>
                            </div>
                        </div>

                        <div class="field col-12 md:col-1" v-if="false"><!-- v-if="detalle.aplica_merma" -->
                            <span><strong>% MERMA: </strong></span>
                            <InputNumber
                                v-model="detalle.merma_porcentaje"
                                suffix="%"
                                :min="0"
                                :max="100"
                                placeholder="0%"
                                :inputClass="'text-right'"
                            />
                        </div>
                        <div class="field col-12 md:col-1" v-else>
                        </div>

                        <div class="field col-12 md:col-1">
                            <Button icon="pi pi-trash" class="p-button-danger" @click="eliminarDetalle(index)" v-tooltip.top="'Quitar insumo'" />
                        </div>
                     </div>
                     <div class="flex justify-content-start mt-2">
                        <Button icon="pi pi-plus" label="AGREGAR INSUMO" class="p-button-info p-button-sm w-auto" @click="agregarDetalle" />
                     </div>
                </div>

            </div>
        </div>

        <template #footer>
            <div class="field">
                <span class="flex md:justify-content-start"><strong>CAMPOS REQUERIDOS: </strong>
                <span class="text-red-500 font-bold ml-1">*</span></span>
            </div>
            <Button label="CANCELAR" icon="pi pi-times" class="p-button-secondary p-button-lg" @click="cerrarModal" />
            <Button
                :label="formulario.id ? 'ACTUALIZAR' : 'GUARDAR'"
                icon="pi pi-save"
                class="p-button-info p-button-lg"
                @click="guardarReceta"
                :loading="guardando"
            />
        </template>
    </Dialog>
</template>

<script setup>
import { ref, reactive, watch } from 'vue';
import { useToast } from 'primevue/usetoast';
import RecetaService from '@/service/RecetaService';
import DropdownProducto from '@/components/shared/Dropdown/DropdownProducto.vue';

const recetaService = new RecetaService();
const toast = useToast();
const errors = ref({});

// Props y Emits para controlar el modal desde el padre (ListaRecetas.vue)
const props = defineProps({
    visible: { type: Boolean, required: true },
    receta: { type: Object, default: () => null }
});
const emit = defineEmits(['update:visible', 'guardado']);

// Estado
const guardando = ref(false);

// Objeto reactivo que coincide exactamente con el RecetaRequest de Laravel
const estadoInicial = {
    producto_id: null,
    descripcion: null,
    // 2026-10: recetas nacen Inactivas. POR QUÉ: con una receta activa el stock del producto pasa a depender de sus
    // insumos (catálogo, polling y venta); se activa a propósito cuando ya está bien cargada. NO afecta a la edición
    // (Object.assign con la receta existente) ni a la BD. Relacionado: PosController::obtenerStockDisponibleProducto.
    estado: false, // nueva receta nace Inactiva (antes: true); no afecta a la edición, que carga la receta existente
    detalles: [
        { producto_id: null, cantidad_gramo: null, cantidad_unidad: null, aplica_merma: true, merma_porcentaje: 0 }
    ]
};

const formulario = reactive(JSON.parse(JSON.stringify(estadoInicial)));

watch(() => props.visible, (newVal) => {
    if (newVal) {
        if (props.receta && props.receta.id) {
            Object.assign(formulario, JSON.parse(JSON.stringify(props.receta)));
        } else {
            Object.assign(formulario, JSON.parse(JSON.stringify(estadoInicial)));
        }
    }
});

// Métodos para el array dinámico
const agregarDetalle = () => {
    formulario.detalles.push({
        producto_id: null,
        cantidad_gramo: null,
        cantidad_unidad: null,
        aplica_merma: true,
        merma_porcentaje: 0
    });
};

const eliminarDetalle = (index) => {
    formulario.detalles.splice(index, 1);
};

// Acciones principales
const cerrarModal = () => {
    Object.assign(formulario, JSON.parse(JSON.stringify(estadoInicial))); // Resetear formulario
    errors.value = {};
    emit('update:visible', false);
};

const guardarReceta = async () => {
    guardando.value = true;
    errors.value = {};
    try {
        let response;
        if (formulario.id) {
            response = await recetaService.actualizarReceta(formulario.id, formulario);
        } else {
            response = await recetaService.guardarReceta(formulario);
        }
        
        if (response && response.errors) {
            errors.value = response.errors;
            toast.add({
                severity: 'error',
                summary: 'Error de validación',
                detail: response.message || 'Corrija los errores en el formulario',
                life: 5000
            });
        } else {
            toast.add({
                severity: 'success',
                summary: 'Éxito',
                detail: 'Receta guardada correctamente',
                life: 3000
            });
            emit('guardado'); // Le avisa al padre que recargue la tabla
            cerrarModal();
        }
    } catch (error) {
        console.error("Error al guardar la receta:", error);

        let detailMessage = 'Ocurrió un error al guardar la receta';

        if (error.response?.data?.message) {
            detailMessage = error.response.data.message;
        } else if (error.response?.data?.errors) {
            const serverErrors = error.response.data.errors;
            const firstError = Object.values(serverErrors).flat()[0];
            if (firstError) detailMessage = firstError;
        } else if (typeof error === 'string') {
            detailMessage = error;
        } else if (error.message) {
            detailMessage = error.message;
        }

        if (error.response?.data?.errors) {
            errors.value = error.response.data.errors;
        } else if (error.errors) {
            errors.value = error.errors;
        }

        toast.add({
            severity: 'error',
            summary: 'Error al guardar',
            detail: detailMessage,
            life: 5000
        });
    } finally {
        guardando.value = false;
    }
};

// TODO: Crear una función onMounted o un watch para cargar las listas de productos e insumos
</script>